import { DestroyRef, Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';
import { ActivatedRouteSnapshot, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UtilisateurService } from './utilisateur.service';
import { Site } from '../bo/site';

/**
 * Custom PostHog event names. Convention: snake_case `<object>_<past-tense verb>`,
 * never a component or method name, so a refactor does not break saved insights.
 */
export const AnalyticsEvent = {
  BlastCreateOpened: 'blast_create_opened',
  BlastCreated:      'blast_created',
  BlastUpdated:      'blast_updated',
  BlastDeleted:      'blast_deleted',
} as const;

/**
 * Initializes PostHog. Called from main.ts before Angular bootstraps, so that errors raised
 * during bootstrap (Keycloak init, APP_INITIALIZERs) are captured too.
 */
export function initPosthog(): void {
  if (!environment.POSTHOG_KEY || !environment.POSTHOG_HOST) {
    return;
  }

  posthog.init(environment.POSTHOG_KEY, {
    api_host: environment.POSTHOG_HOST,
    person_profiles: 'identified_only',
    defaults: '2025-05-24',
    enable_heatmaps: true,
    capture_pageview: false,
    capture_exceptions: true,
  });

  // Groups are persisted by PostHog: drop the previous session's site until one is selected again.
  posthog.resetGroups();
}

@Injectable({ providedIn: 'root' })
export class PosthogService {
  constructor(
    private router: Router,
    private destroyRef: DestroyRef,
    private utilisateurService: UtilisateurService,
  ) {
    if (posthog.__loaded) {
      this.trackNavigationAndGroups();
    }
  }

  private trackNavigationAndGroups(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // The routed site is set asynchronously, after NavigationEnd: the groups registered so far
        // can still be the previous site's, so a site route sets them explicitly on its pageview.
        const siteId = this.routedSiteId();
        posthog.capture('$pageview', siteId ? { $groups: this.siteGroups(siteId) } : undefined);
      });

    // Attach every following event to the current organization and site (group analytics).
    this.utilisateurService.currentSite$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(site => {
        if (!site?.siteId) {
          posthog.resetGroups();
          return;
        }
        const orgId = orgIdOf(site);
        if (orgId) {
          posthog.group('organization', String(orgId));
        }
        posthog.group('site', String(site.siteId), { name: site.nom, orgId });
      });
  }

  /** Site id carried by the current route (`site/:siteId`, `site-setup/:siteId`, `mysite/:id`), if any. */
  private routedSiteId(): number | null {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      const id = route.paramMap.get('siteId')
        ?? (route.routeConfig?.path === 'mysite/:id' ? route.paramMap.get('id') : null);
      if (id && +id) {
        return +id;
      }
      route = route.firstChild;
    }
    return null;
  }

  /** Groups of a site; the organization is left out while the site list is not loaded yet. */
  private siteGroups(siteId: number): Record<string, string> {
    const site = this.utilisateurService.listeSites?.find(s => s.siteId == siteId);
    const orgId = site ? orgIdOf(site) : null;
    return orgId
      ? { organization: String(orgId), site: String(siteId) }
      : { site: String(siteId) };
  }
}

// `Sites/{id}` returns `orgId: 0` on the site itself: the organization is the area's.
function orgIdOf(site: Site): number | null {
  return site.region?.orgId || site.orgId || null;
}
